import { IVector3 } from '@seddi/math-v2';
import { Euler, MathUtils, Object3D, PerspectiveCamera, Vector3 } from '@seddi/three';

type IRoot = { position: Vector3; rotation: Euler };

type CameraConstructorArgs = {
  distance?: number;
  fov?: number;
  origin?: IVector3;
  pitch?: number;
  yaw?: number;
};

export class Camera extends PerspectiveCamera {
  root: Object3D;
  initial: { root: IRoot; fov: number; distance: number } & IRoot;

  /**
   * Creates a new 3D camera
   * @param args Constructor arguments that contains information about camera field-of-view, origin, yaw and pitch angles and distance
   */
  constructor({
    fov = 60,
    origin = { x: 0, y: 1, z: 0 },
    yaw = 0,
    pitch = Math.PI * -0.1,
    distance = 2.2,
  }: CameraConstructorArgs = {}) {
    super(fov, 1, 0.01, 60);
    this.root = new Object3D();
    this.root.rotation.order = 'YXZ';
    this.root.rotation.y = yaw;
    this.root.rotation.x = pitch;
    this.root.position.set(origin.x, origin.y, origin.z);
    this.root.add(this);
    this.position.z = distance;
    this.initial = {
      position: this.position.clone(),
      rotation: this.rotation.clone(),
      root: {
        position: this.root.position.clone(),
        rotation: this.root.rotation.clone(),
      },
      fov,
      distance,
    };
  }

  /**
   * Increases the camera zoom with given amount of increment
   * @param inc Amount of increment
   */
  incZoom(inc: number): void {
    const { position, initial } = this;

    // Adjust zooming to actual fov compared to old fixed 60
    const fovRatio = this.getFovRatio(initial.fov);

    inc *= fovRatio;
    const MAX_ZOOM_OUT_RANGE = 2.8 * fovRatio;
    const MAX_CAMERA_DISTANCE = 20 * fovRatio;
    const MIN_CAMERA_DISTANCE = 0.1 * fovRatio;
    const DEFAULT_MID_VALUE = 2.2 * fovRatio;

    const scale = initial.distance / DEFAULT_MID_VALUE;
    const highEnd = Math.min(MAX_CAMERA_DISTANCE, initial.distance + MAX_ZOOM_OUT_RANGE);
    const minZoom = Math.log(MIN_CAMERA_DISTANCE);
    const maxZoom = Math.log(highEnd * scale);
    const range = maxZoom - minZoom;
    const logZoom = Math.log(position.z);
    let zoom = (logZoom - minZoom) / range;
    zoom = Math.min(Math.max(zoom + inc, 0), 1);
    position.z = Math.exp(minZoom + zoom * range);
  }

  /**
   * Resets the camera position and rotation to the initial state
   */
  reset(): void {
    const { initial, root } = this;
    root.position.copy(initial.root.position);
    root.rotation.copy(initial.root.rotation);
    this.position.copy(initial.position);
    this.rotation.copy(initial.rotation);
  }

  /**
   * Rotates the camera in the Y-direction with given amount of rotation
   * @param degrees Amount of rotation
   */
  rotate(degrees: number): void {
    const { root } = this;
    root.rotation.y += MathUtils.degToRad(degrees);
  }

  private getFovRatio(fov: number) {
    const baseFov = 60;
    if (fov === baseFov) return 1;
    const initialDepthRatio = Math.tan((baseFov / 2) * (Math.PI / 180)) * 2;
    const currentDepthRatio = Math.tan((fov / 2) * (Math.PI / 180)) * 2;
    const fovRatio = initialDepthRatio / currentDepthRatio;
    return fovRatio;
  }
}

export default Camera;
